import React, { useCallback } from 'react';
import { useLinkTo } from '@react-navigation/native';
import { type To } from '@react-navigation/native/lib/typescript/src/useLinkTo';
import { ProductBannerCardWithOverlay, useSafeRouter } from '@sg/garnish';

import { useCustomProductContent } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const MenuCategoriesGridCustomProductCard = (
  props: MenuContentProductCardProps,
) => {
  const { product, generateLink, generateHref } = props;

  const router = useSafeRouter();
  const linkTo = useSafeLinkTo();

  // ─── Remote Data ─────────────────────────────────────────────────────

  const { content } = useCustomProductContent({
    customProductType: product.customType,
  });

  // ─── Helpers ─────────────────────────────────────────────────────────

  const navigate = useCallback(() => {
    const href = generateHref?.(product.slug);

    if (href) {
      router?.navigate(href);
      return;
    }

    linkTo?.(generateLink(product.slug));
  }, [generateHref, generateLink, linkTo, product.slug, router]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ProductBannerCardWithOverlay
      title={content.heading}
      description={content.body}
      onCtaPress={navigate}
      ctaLabel={content.buttonLabel}
      ctaPalette={content.buttonPalette}
      isDisabled={product.outOfStock}
      backgroundImage={content.backgroundImage}
      backgroundOverlayColor={content.backgroundOverlayColor}
      textColor={content.textColor}
    />
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type MenuContentProductCardProps = {
  product: {
    slug: string;
    outOfStock: boolean;
    customType?: string | null;
  };
  generateLink: (productSlug: string) => To;
  generateHref?: (productSlug: string) => string;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function useSafeLinkTo() {
  try {
    return useLinkTo();
  } catch {
    return null;
  }
}
